/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Module-scoped stash for the anti-bot challenge step-up flow.
 *
 * The solved CHECKBOX token never touches React state (D13) -- it passes
 * through this slot exactly once, from the widget's callback to the single
 * retried request that redeems it. A second 428 arriving while one challenge
 * is already pending joins the same deferred instead of opening a second
 * widget (stampede protection); the request layer awaits this same gate
 * before minting its own token so a burst of queued requests doesn't pile
 * up more 428s during the challenge.
 */

const TOKEN_MAX_AGE_MS = 100_000; // headroom under the backend's 120s freshness window (D4)

// Safety net for awaitChallengeSettled(): if a challenge is ever orphaned --
// its overlay fails to mount, the widget silently never calls back, or any
// other path that leaves `pending` set without ever resolving/rejecting it --
// every subsequent non-safe-method request app-wide awaits this gate in
// OpenAPI.ts's header resolver and would otherwise hang forever. Mirrors the
// same bounded-wait pattern used by AuthGate (15s) and the reCAPTCHA execute
// timeout (8s) elsewhere in this codebase.
const SETTLE_TIMEOUT_MS = 20_000;

export class AntiBotChallengeCancelledError extends Error {
  constructor(reason?: string) {
    super(reason || 'Anti-bot challenge was cancelled');
    this.name = 'AntiBotChallengeCancelledError';
  }
}

interface PendingChallenge {
  siteKey: string;
  ticket: string | null;
  promise: Promise<string>;
  resolve: (token: string) => void;
  reject: (err: Error) => void;
}

interface SolvedToken {
  token: string | null;
  ticket: string | null;
  mintedAt: number;
}

let pending: PendingChallenge | null = null;
let solved: SolvedToken | null = null;

/**
 * Starts a challenge for `siteKey`, or joins the one already in flight.
 *
 * @param ticket Optional Fase 6 HMAC ticket from the 428 body's
 *   `challenge_ticket` field, echoed back on the redemption request. Older
 *   backends that don't issue one simply omit it -- everything still works,
 *   the ticket is non-blocking hardening (see consumeChallengeTicket).
 * @returns A promise that resolves with the solved token, or rejects with
 *          {@link AntiBotChallengeCancelledError} if the challenge is cancelled.
 */
export function startChallenge(siteKey: string, ticket: string | null = null): Promise<string> {
  if (pending) return pending.promise;

  let resolveFn!: (token: string) => void;
  let rejectFn!: (err: Error) => void;
  const promise = new Promise<string>((resolve, reject) => {
    resolveFn = resolve;
    rejectFn = reject;
  });
  pending = { siteKey, ticket, promise, resolve: resolveFn, reject: rejectFn };
  return promise;
}

/**
 * Site key of the in-flight challenge, or null when none is pending.
 */
export function getPendingSiteKey(): string | null {
  return pending?.siteKey ?? null;
}

/**
 * Whether a challenge is currently pending resolution.
 */
export function hasPendingChallenge(): boolean {
  return pending !== null;
}

/**
 * Called from the CHECKBOX widget's solve callback with the raw token.
 */
export function resolveChallenge(token: string): void {
  if (!pending) return;
  const { resolve, ticket } = pending;
  pending = null;
  solved = { token, ticket, mintedAt: Date.now() };
  resolve(token);
}

/**
 * Cancels the pending challenge (e.g. the user closed the overlay).
 */
export function cancelChallenge(reason?: string): void {
  if (!pending) return;
  const { reject } = pending;
  pending = null;
  reject(new AntiBotChallengeCancelledError(reason));
}

/**
 * Consumes the most recently solved token exactly once. Returns null on a
 * repeat call, and null when the token has aged past {@link TOKEN_MAX_AGE_MS}
 * -- a stale token would just be rejected by the backend's own freshness
 * check anyway, so failing fast here skips a doomed network round-trip.
 *
 * Consuming the token does not affect {@link consumeChallengeTicket} --
 * callers read both together to build the redemption request, and either
 * order must work.
 */
export function consumeChallengeToken(): string | null {
  return _consumeSolvedField('token');
}

/**
 * Consumes the ticket paired with the most recently solved token (Fase 6),
 * exactly once. Null when no ticket was issued (older backends) or on a
 * repeat call -- see {@link consumeChallengeToken}, which this mirrors.
 */
export function consumeChallengeTicket(): string | null {
  return _consumeSolvedField('ticket');
}

/**
 * Consumes one field (`token` or `ticket`) of the most recently solved
 * challenge exactly once, clearing the whole record once both are consumed.
 *
 * @param field - Which field to consume.
 * @returns The field's value if still fresh, otherwise null.
 */
function _consumeSolvedField(field: 'token' | 'ticket'): string | null {
  if (!solved) return null;
  const fresh = Date.now() - solved.mintedAt <= TOKEN_MAX_AGE_MS;
  const value = solved[field];
  solved[field] = null;
  if (solved.token === null && solved.ticket === null) {
    solved = null;
  }
  return fresh ? value : null;
}

/**
 * Resolves once any pending challenge has settled, solved or cancelled --
 * queued requests await this before minting their own token so they don't
 * fire mid-challenge and pile up more 428s.
 *
 * Bounded by {@link SETTLE_TIMEOUT_MS}: an orphaned challenge (overlay never
 * mounted, widget never calls back) must not permanently wedge every other
 * request in the app. On timeout the gate simply opens -- the caller mints
 * its own score token normally, so this never bypasses the actual anti-bot
 * check, it only stops queueing behind a challenge that is never coming.
 */
export async function awaitChallengeSettled(): Promise<void> {
  if (!pending) return;
  const current = pending;
  await Promise.race([
    current.promise.catch(() => {
      // Callers only need to know the gate is clear, not why it cleared.
    }),
    new Promise<void>((resolve) => setTimeout(resolve, SETTLE_TIMEOUT_MS)),
  ]);
}
